import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["src"]
const _hoisted_2 = {
  key: 1,
  class: "builderModal_draft",
  "data-test": "draft"
}
const _hoisted_3 = {
  key: 1,
  class: "builderModal_top",
  "data-drag-zone": ""
}
const _hoisted_4 = {
  key: 0,
  class: "builderModal_draft",
  "data-test": "draft"
}
const _hoisted_5 = { class: "builderModal_body" }
const _hoisted_6 = {
  key: 0,
  class: "builderModal_heading",
  "data-test": "heading"
}
const _hoisted_7 = ["innerHTML"]
const _hoisted_8 = {
  key: 2,
  class: "builderModal_buttons"
}

import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import type { PropType } from 'vue'
import { useMq } from 'vue3-mq'

import AcButton from '@ui-kit/components/AcButton.vue'
import AcModal from '@ui-kit/components/AcModal.vue'
import { IMAGE_TIMEOUT_MS } from '../constants'
import { preloadImage } from '../imagePreload'
import { sanitizeHtml } from '../sanitizeHtml'
import type { ButtonStyle, CloseMethod, ModalButton, ModalRule } from '../types'
import BuilderModalClose from './BuilderModalClose.vue'
import BuilderModalSkeleton from './BuilderModalSkeleton.vue'

// Свайп вниз дальше этого — закрытие шторки (как тап по оверлею).
const SWIPE_CLOSE_DISTANCE = 80

// Каталог кнопок конструктора → вариант AcButton. Чего у AcButton нет (цвета ghost/alert/warning,
// акцентный текст ghost_stroke) — переопределяется ниже в стилях этого компонента, не в AcButton.

export default /*@__PURE__*/_defineComponent({
  __name: 'BuilderModal',
  props: {
  rule: { type: Object as PropType<ModalRule>, required: true },
  loadImage: { type: Function as PropType<(url: string) => Promise<boolean>>, default: preloadImage },
  imageTimeoutMs: { type: Number, default: IMAGE_TIMEOUT_MS }
},
  emits: ["shown", "close", "cta"],
  setup(__props, { emit: __emit }) {

const AC_BUTTON_VARIANT: Record<ButtonStyle, 'outlined' | 'text' | undefined> = {
  active: undefined,
  secondary: 'outlined',
  ghost_stroke: 'text',
  ghost: undefined,
  alert: undefined,
  warning: undefined
}

const props = __props

const emit = __emit

const mq = useMq()
const content = computed(() => props.rule.content)
const safeText = computed(() => (content.value.text ? sanitizeHtml(content.value.text) : ''))

// skeleton → ready. ready = видны текст и кнопки; ровно в этот момент — shown.
const phase = ref<'skeleton' | 'ready'>('skeleton')
const imageLoaded = ref(false)
let timer: ReturnType<typeof setTimeout> | undefined

function toReady (): void {
  if (phase.value === 'ready') return
  phase.value = 'ready'
  emit('shown')
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
  const url = content.value.imageUrl
  if (!url) {
    toReady()
    return
  }
  timer = setTimeout(toReady, props.imageTimeoutMs)
  props.loadImage(url)
    .catch(() => false)
    .then(ok => {
      imageLoaded.value = ok
      toReady()
    })
})

function onKeydown (e: KeyboardEvent): void {
  if (e.key === 'Escape') emit('close', 'overlay')
}

onBeforeUnmount(() => {
  if (timer) clearTimeout(timer)
  document.removeEventListener('keydown', onKeydown)
})

// AcModal шлёт update:modelValue=false по клику на оверлей.
function onModelValue (value: boolean): void {
  if (!value) emit('close', 'overlay')
}

// Свайп — только у шторки (мобильная ширина).
const dragStartY = ref<number | null>(null)
const dragOffset = ref(0)
const sheetStyle = computed(() => (dragOffset.value > 0 ? { transform: `translateY(${dragOffset.value}px)`, transition: 'none' } : undefined))

function onPointerDown (e: PointerEvent): void {
  if (mq.current === 'desktop') return
  if (!(e.target instanceof Element) || !e.target.closest('[data-drag-zone]')) return
  dragStartY.value = e.clientY
}

function onPointerMove (e: PointerEvent): void {
  if (dragStartY.value === null) return
  dragOffset.value = Math.max(0, e.clientY - dragStartY.value)
}

function onPointerUp (): void {
  if (dragStartY.value === null) return
  const shouldClose = dragOffset.value > SWIPE_CLOSE_DISTANCE
  dragStartY.value = null
  dragOffset.value = 0
  if (shouldClose) emit('close', 'overlay')
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(AcModal, {
    "model-value": true,
    "mobile-modal-type": "bottomSheet",
    "onUpdate:modelValue": onModelValue
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", {
        class: "builderModal",
        "data-test": "sheet",
        style: _normalizeStyle(sheetStyle.value),
        onPointerdown: onPointerDown,
        onPointermove: onPointerMove,
        onPointerup: onPointerUp,
        onPointercancel: onPointerUp
      }, [
        (phase.value === 'skeleton')
          ? (_openBlock(), _createBlock(BuilderModalSkeleton, {
              key: 0,
              "show-close": content.value.showClose,
              onClose: _cache[0] || (_cache[0] = ($event: any) => (emit('close', 'close')))
            }, null, 8, ["show-close"]))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              (content.value.imageUrl)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: _normalizeClass(['builderModal_image', { '-loaded': imageLoaded.value }]),
                    "data-test": "image",
                    "data-drag-zone": ""
                  }, [
                    (imageLoaded.value)
                      ? (_openBlock(), _createElementBlock("img", {
                          key: 0,
                          class: "builderModal_img",
                          src: content.value.imageUrl,
                          alt: ""
                        }, null, 8, _hoisted_1))
                      : _createCommentVNode("", true),
                    (__props.rule.isDraft)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_2, "черновик"))
                      : _createCommentVNode("", true),
                    (content.value.showClose)
                      ? (_openBlock(), _createBlock(BuilderModalClose, {
                          key: 2,
                          "data-test": "close",
                          onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('close', 'close')))
                        }))
                      : _createCommentVNode("", true)
                  ], 2))
                : (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    (__props.rule.isDraft)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_4, "черновик"))
                      : _createCommentVNode("", true),
                    (content.value.showClose)
                      ? (_openBlock(), _createBlock(BuilderModalClose, {
                          key: 1,
                          "on-white": "",
                          "data-test": "close",
                          onClick: _cache[2] || (_cache[2] = ($event: any) => (emit('close', 'close')))
                        }))
                      : _createCommentVNode("", true)
                  ])),
              _createElementVNode("div", _hoisted_5, [
                (content.value.heading)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(content.value.heading), 1))
                  : _createCommentVNode("", true),
                (safeText.value)
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 1,
                      class: "builderModal_text",
                      "data-test": "text",
                      innerHTML: safeText.value
                    }, null, 8, _hoisted_7))
                  : _createCommentVNode("", true),
                (content.value.buttons.length)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(content.value.buttons, (button) => {
                        return (_openBlock(), _createBlock(AcButton, {
                          key: button.id,
                          block: "",
                          variant: AC_BUTTON_VARIANT[button.style],
                          class: _normalizeClass(['builderModal_button', `-${button.style}`]),
                          "data-test": "button",
                          onClick: ($event: any) => (emit('cta', button))
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(button.text), 1)
                          ]),
                          _: 2
                        }, 1032, ["variant", "class", "onClick"]))
                      }), 128))
                    ]))
                  : _createCommentVNode("", true)
              ])
            ], 64))
      ], 36)
    ]),
    _: 1
  }))
}
}

})